import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "./auth";

export interface CartProduct {
  id: number;
  slug: string;
  title: string;
  category: "print" | "original";
  image: string;
  price: number;
  currency: string;
  stockQuantity?: number;
  printSize?: string;
}

export interface CartItem extends CartProduct {
  quantity: number;
}

interface CartContextValue {
  items: CartItem[];
  itemCount: number;
  subtotal: number;
  isOpen: boolean;
  addItem: (product: CartProduct) => void;
  removeItem: (id: number, printSize?: CartProduct["printSize"]) => void;
  updateQuantity: (id: number, quantity: number, printSize?: CartProduct["printSize"]) => void;
  clearCart: () => void;
  openCart: () => void;
  closeCart: () => void;
}

const CartContext = createContext<CartContextValue | null>(null);
const LEGACY_STORAGE_KEY = "dalilaaint-cart";
const GUEST_STORAGE_KEY = "dalila-cart-guest";
const storageKeyForUser = (userId: number) => `dalila-cart-user-${userId}`;

function readCart(storageKey: string): CartItem[] {
  try {
    const stored = localStorage.getItem(storageKey);
    const parsed = stored ? JSON.parse(stored) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function CartProvider({ children }: { children: React.ReactNode }) {
  const { user, initialized } = useAuth();
  const [items, setItems] = useState<CartItem[]>(() => readCart(LEGACY_STORAGE_KEY));
  const [storageKey, setStorageKey] = useState(LEGACY_STORAGE_KEY);
  const [isOpen, setIsOpen] = useState(false);
  const previousUserId = useRef<number | null | undefined>(undefined);

  useEffect(() => {
    if (!initialized) return;
    const userId = user?.id ?? null;
    const justLoggedOut = previousUserId.current !== undefined && previousUserId.current !== null && userId === null;
    previousUserId.current = userId;

    if (justLoggedOut) {
      setStorageKey(GUEST_STORAGE_KEY);
      setItems([]);
      setIsOpen(false);
      return;
    }

    if (!user) {
      const guestCart = readCart(GUEST_STORAGE_KEY);
      setStorageKey(GUEST_STORAGE_KEY);
      setItems(guestCart);
      return;
    }

    const userStorageKey = storageKeyForUser(user.id);
    let userCart = readCart(userStorageKey);
    // Preserve a cart created before account-specific carts existed for the
    // currently signed-in customer, then remove the shared legacy copy.
    if (!userCart.length && localStorage.getItem(userStorageKey) === null) {
      userCart = readCart(LEGACY_STORAGE_KEY);
      if (userCart.length) localStorage.setItem(userStorageKey, JSON.stringify(userCart));
      localStorage.removeItem(LEGACY_STORAGE_KEY);
    }
    setStorageKey(userStorageKey);
    setItems(userCart);
  }, [initialized, user]);

  useEffect(() => localStorage.setItem(storageKey, JSON.stringify(items)), [items, storageKey]);

  const value = useMemo<CartContextValue>(() => ({
    items,
    itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
    subtotal: items.reduce((sum, item) => sum + item.price * item.quantity, 0),
    isOpen,
    addItem: (product) => {
      setItems((current) => {
        const existing = current.find((item) => item.id === product.id && item.printSize === product.printSize);
        if (!existing) return [...current, { ...product, quantity: 1 }];
        if (existing) return current;
      });
      setIsOpen(true);
    },
    removeItem: (id, printSize) => setItems((current) => current.filter((item) => item.id !== id || item.printSize !== printSize)),
    updateQuantity: (id, quantity, printSize) => setItems((current) => current.flatMap((item) => {
      if (item.id !== id || item.printSize !== printSize) return [item];
      if (quantity <= 0) return [];
      return [{ ...item, quantity: 1 }];
    })),
    clearCart: () => setItems([]),
    openCart: () => setIsOpen(true),
    closeCart: () => setIsOpen(false),
  }), [items, isOpen]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const value = useContext(CartContext);
  if (!value) throw new Error("useCart must be used inside CartProvider");
  return value;
}
